/**
 * "AI Generated Summary" (design §4, full-screening only): a WHITE icon square
 * with a 1px light border — unique on this page, NOT a pastel square — holding
 * a dark sparkle, over the AI narrative paragraph. Renders null while the
 * payload carries no narrative (data-map §3: the presence check lives here,
 * INSIDE the page's `screeningKind === 'full'` gate at the render site).
 *
 * Since 2026-08-27 it renders as the first row INSIDE the summary-tiles card
 * (SummaryTilesView's `aiSummary` slot), with the Rental Range / Move-In /
 * Savings tiles as the second row. It keeps the shared `DETAIL_CARD` chrome —
 * same radius, background and shadow as the card wrapping it — because a
 * bordered tile read as a different design language next to its own parent
 * (client 2026-08-27). Left-aligned rather than centered: it holds a
 * paragraph, not a stat. The bottom margin that separates the two rows lives
 * here rather than on the grid, because only this component knows whether it
 * rendered anything at all — a summary-less payload must not leave the tiles
 * with a stray gap above them.
 */
import { Sparkle } from 'lucide-react'
import { resolveAiSummaryText } from '#app/utils/application-detail-v2/report-display'
import { cn } from '#app/utils/misc'
import { DETAIL_CARD } from '../primitives'

/** Presentational body shared with the combined page (typed text in). */
export function AiSummaryBody({ text }: { text: string | null }) {
	if (!text) return null

	return (
		<div className={cn(DETAIL_CARD, 'mb-6 p-6 sm:p-7')}>
			<div className="flex items-center gap-3">
				<span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#FBFDFD] shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
					<Sparkle aria-hidden="true" className="size-5 text-[#32302F]" />
				</span>
				<h2 className="text-[16px] font-medium text-[#32302F]">
					AI Generated Summary
				</h2>
			</div>
			<p className="mt-4 text-[16px] leading-relaxed text-[#32302F]">{text}</p>
		</div>
	)
}

export function AiSummary({
	aiReportData,
	aiGeneratedPilot = false,
}: {
	aiReportData: Record<string, unknown> | null
	/** Pilot tenants (Rentpedia) prefer the AI backend's new
	 * `aiGeneratedSummary` field over the classic `summary`. */
	aiGeneratedPilot?: boolean
}) {
	return (
		<AiSummaryBody
			text={resolveAiSummaryText({ aiReportData, aiGeneratedPilot })}
		/>
	)
}
